import { FC, useState } from "react";
import { Box, Button, Collapse, IconButton, Typography } from "@mui/material";
import {
  ArrowDropDown,
  ArrowDropUp,
  Check,
  SaveAlt,
} from "@mui/icons-material";
import { makeSxStyles } from "ui";
import { DateTime } from "luxon";
import { AdminLocationPlowSkipReportResDto } from "service/api/estabrookSchemas";

interface SkippedOrdersPerSeasonProps {
  report?: AdminLocationPlowSkipReportResDto;
  isLoading?: boolean;
  /** Opens the plow-report date-range dialog for this location. */
  onDownloadReport?: () => void;
}

// "2026-04-05" -> "05 Apr 2026" (per Figma).
const formatEntryDate = (isoDate: string): string => {
  const parsed = DateTime.fromISO(isoDate);
  return parsed.isValid ? parsed.toFormat("dd LLL yyyy") : isoDate;
};

/**
 * "Skipped orders per season" card for the customer location detail.
 * Collapsed by default: shows a per-season summary + Download Report; expands
 * to a Date / Customer / Operator table of skip events.
 */
const SkippedOrdersPerSeason: FC<SkippedOrdersPerSeasonProps> = ({
  report,
  isLoading,
  onDownloadReport,
}) => {
  const [expanded, setExpanded] = useState(false);
  const styles = useStyles();

  if (!report) return null;

  const { summary, entries } = report;

  return (
    <Box sx={styles.row}>
      <Box sx={styles.labelCol}>
        <Typography sx={styles.label}>Skipped orders per season</Typography>
      </Box>
      <Box sx={styles.contentCol}>
        <Box sx={styles.header}>
          <Typography sx={styles.summary}>
            {`${summary.customerCount} by Customer  ·  ${summary.operatorCount} by Operator`}
          </Typography>
          <IconButton
            size="small"
            sx={styles.toggle}
            onClick={() => setExpanded((prev) => !prev)}
            disabled={isLoading}
            aria-label={
              expanded ? "Collapse skipped orders" : "Expand skipped orders"
            }
            data-testid="skipped-orders-toggle"
          >
            {expanded ? <ArrowDropUp /> : <ArrowDropDown />}
          </IconButton>
        </Box>

        <Collapse in={expanded} unmountOnExit>
          <Box sx={styles.panel}>
            <Box sx={styles.tableHeader}>
              <Typography sx={styles.headerCellDate}>Date</Typography>
              <Typography sx={styles.headerCellCenter}>
                {`Customer (${summary.customerCount})`}
              </Typography>
              <Typography sx={styles.headerCellCenter}>
                {`Operator (${summary.operatorCount})`}
              </Typography>
            </Box>
            <Box sx={styles.tableBody}>
              {entries.map((entry) => (
                <Box key={entry.date} sx={styles.tableRow}>
                  <Typography sx={styles.dateCell}>
                    {formatEntryDate(entry.date)}
                  </Typography>
                  <Box sx={styles.checkCell}>
                    {entry.customerSkipped && (
                      <Check
                        sx={styles.check}
                        titleAccess="Skipped by customer"
                      />
                    )}
                  </Box>
                  <Box sx={styles.checkCell}>
                    {entry.operatorSkipped && (
                      <Check
                        sx={styles.check}
                        titleAccess="Skipped by operator"
                      />
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Collapse>

        <Button
          variant="text"
          endIcon={<SaveAlt sx={styles.downloadIcon} />}
          onClick={onDownloadReport}
          disabled={!onDownloadReport}
          sx={styles.downloadButton}
          data-testid="download-skipped-orders-report"
        >
          Download Report
        </Button>
      </Box>
    </Box>
  );
};

const useStyles = makeSxStyles(() => ({
  // Aligns with the FieldRow / Address Info grid (label 260 / gap 24 / content).
  row: {
    gridColumn: "1 / -1",
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    gap: "24px",
  },
  labelCol: {
    width: "260px",
    flexShrink: 0,
    pt: "10px",
  },
  label: {
    fontWeight: 600,
    fontSize: "14px",
    lineHeight: "20px",
    color: "#c5c6c9",
  },
  contentCol: {
    flex: "1 1 auto",
    maxWidth: "400px",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
  },
  summary: {
    fontWeight: 500,
    fontSize: "14px",
    lineHeight: "20px",
    color: "#c1c7ce",
    whiteSpace: "pre-wrap",
  },
  toggle: {
    color: "#c1c7ce",
    p: "2px",
  },
  panel: {
    // Collapsed: column gap is 0 so Download Report hugs the header (per Figma
    // 14170:81215). Expanded: these margins restore the 12px gaps around the
    // panel (per Figma 14139:55045). Margins sit inside MUI Collapse's inner
    // wrapper, so they only show when expanded.
    my: "12px",
    backgroundColor: "#1a1c1e",
    borderRadius: "20px",
    p: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  tableHeader: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  headerCellDate: {
    flex: "1 1 0",
    minWidth: 0,
    fontWeight: 500,
    fontSize: "12px",
    lineHeight: "16px",
    color: "#c1c7ce",
  },
  headerCellCenter: {
    flex: "1 1 0",
    minWidth: 0,
    textAlign: "center",
    fontWeight: 500,
    fontSize: "12px",
    lineHeight: "16px",
    color: "#c1c7ce",
  },
  tableBody: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  tableRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  dateCell: {
    flex: "1 1 0",
    minWidth: 0,
    fontWeight: 500,
    fontSize: "12px",
    lineHeight: "16px",
    color: "#8b9198",
  },
  checkCell: {
    flex: "1 1 0",
    minWidth: 0,
    minHeight: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  check: {
    color: "#8aceff",
    fontSize: "20px",
  },
  downloadButton: {
    alignSelf: "flex-start",
    p: 0,
    color: "#8aceff",
    textTransform: "none",
    fontWeight: 500,
    fontSize: "14px",
    lineHeight: "20px",
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  downloadIcon: {
    fontSize: "18px",
  },
}));

export default SkippedOrdersPerSeason;
